import { useCallback, useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { Check, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { usePlanUi, type PlanKey } from "@/hooks/usePlanUi";
import { blockStaffCheckout } from "@/lib/checkoutRouter";
import { changePlanSubscription, getPlanSubscription, startPlanCheckout } from "@/lib/planBilling.functions";

type Source = "header" | "quota_modal" | "locked_module" | "pricing" | "billing";
const euros = (c: number) => (c / 100).toLocaleString("es-ES", { maximumFractionDigits: 2 });

/**
 * Subir de plan desde cualquier punto (cabecera, modales, módulos, Mi cuenta).
 * Muestra lo que se gana según la matriz publicada y confirma antes de cobrar.
 */
export function usePlanUpgrade() {
  const ui = usePlanUi();
  const [target, setTarget] = useState<{ plan: PlanKey; source: Source } | null>(null);
  const requestUpgrade = useCallback((plan: PlanKey, source: Source) => setTarget({ plan, source }), []);
  const dialog = <PlanUpgradeDialog ui={ui} target={target} onClose={() => setTarget(null)} />;
  return { requestUpgrade, dialog };
}

function PlanUpgradeDialog({ ui, target, onClose }: { ui: ReturnType<typeof usePlanUi>; target: { plan: PlanKey; source: Source } | null; onClose: () => void }) {
  const es = ui.es;
  const [busy, setBusy] = useState(false);
  const getStatus = useServerFn(getPlanSubscription);
  const checkout = useServerFn(startPlanCheckout);
  const change = useServerFn(changePlanSubscription);
  const p = ui.plans.find((x) => x.plan_key === target?.plan);
  const gains = target ? ui.diff(ui.planKey, target.plan) : [];
  const trial = !!p?.trial_enabled && (p?.trial_days ?? 0) > 0;

  const [status, setStatus] = useState<any>(null);
  useEffect(() => {
    if (!target) { setStatus(null); return; }
    let off = false;
    getStatus().then((r) => { if (!off) setStatus(r); }).catch(() => {});
    return () => { off = true; };
  }, [target, getStatus]);
  const sub = status?.subscription ?? null;
  const inTrial = sub?.status === "trialing" && !!sub?.trial_end;
  const daysLeft = inTrial ? Math.max(0, Math.ceil((sub.trial_end * 1000 - Date.now()) / 86400000)) : 0;
  const trialEndDate = inTrial ? new Date(sub.trial_end * 1000).toLocaleDateString(es ? "es-ES" : "en-GB", { day: "numeric", month: "long" }) : "";
  // Prueba solo para quien nunca ha tenido una; si ya tiene suscripción, no hay prueba nueva.
  const showNewTrial = trial && !sub;

  const confirm = async () => {
    if (!target || !p) return;
    if (await blockStaffCheckout()) { onClose(); return; }
    setBusy(true);
    // Abrir la pestaña antes del await para que el navegador no la bloquee.
    const win = typeof window !== "undefined" ? window.open("", "_blank") : null;
    try {
      const status = await getStatus();
      if (status.subscription) {
        win?.close();
        const r: any = await change({ data: { action: "change", plan_key: target.plan as any, source: target.source } });
        if (r.kind === "needs_card" && r.url) { window.location.href = r.url; return; }
        if (r.kind === "upgrade") void import("@/lib/planTracking").then((m) => m.trackSubscriptionUpgrade(target.plan)).catch(() => {});
        toast.success(r.kind === "upgrade"
          ? (es ? `Ya estás en ${ui.planName(target.plan)}.` : `You're now on ${ui.planName(target.plan)}.`)
          : (es ? "Cambio programado para el final del periodo." : "Change scheduled for the end of the period."));
      } else {
        const r = await checkout({ data: { plan_key: target.plan as any, interval: "month", source: target.source } });
        void import("@/lib/analytics").then((m) => m.trackBeginCheckout({ source: String(target.source), productType: "plan", productKey: target.plan, interval: "month", valueCents: trial ? 0 : p.price_monthly_cents, itemName: ui.planName(target.plan) })).catch(() => {});
        if (r.url) { if (win) win.location.href = r.url; else window.location.href = r.url; }
      }
      ui.refresh();
      onClose();
    } catch (e) {
      win?.close();
      console.error("[plan-upgrade]", e);
      toast.error(es ? "No hemos podido abrir el cambio de plan. Inténtalo en un momento." : "We couldn't change your plan. Please try again shortly.");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={!!target} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display">
            {es ? `Sube a ${ui.planName(target?.plan)}` : `Upgrade to ${ui.planName(target?.plan)}`}
            {p ? ` · ${euros(p.price_monthly_cents)} €${es ? "/mes" : "/mo"}` : ""}
          </DialogTitle>
          <DialogDescription>
            {inTrial
              ? (es ? `Pasas a ${ui.planName(target?.plan)} ahora. Te quedan ${daysLeft} días gratis; el ${trialEndDate} se cobran ${euros(p?.price_monthly_cents ?? 0)} €. No se empieza una prueba nueva.` : `You move to ${ui.planName(target?.plan)} now. You have ${daysLeft} free days left; on ${trialEndDate} you'll be charged €${euros(p?.price_monthly_cents ?? 0)}. No new trial starts.`)
              : showNewTrial
              ? p!.trial_end_behavior === "charge"
                ? (es ? `${p!.trial_days} días gratis. Pedimos tarjeta y te avisamos antes de que acabe; si no cancelas, se cobra al terminar. Sin permanencia.` : `${p!.trial_days} days free. Card required; we remind you before it ends and charge only if you don't cancel. No commitment.`)
                : (es ? `${p!.trial_days} días gratis, sin tarjeta. Sin permanencia.` : `${p!.trial_days} days free, no card needed. No commitment.`)
              : (es ? "Si ya tienes un plan, hoy solo pagas la diferencia proporcional. Sin permanencia." : "If you already have a plan, today you only pay the prorated difference. No commitment.")}
          </DialogDescription>
        </DialogHeader>
        {gains.length > 0 && (
          <div>
            <p className="mb-2 font-body text-xs font-semibold uppercase tracking-wide text-muted-foreground">{es ? "Lo que ganas" : "What you get"}</p>
            <ul className="max-h-64 space-y-1.5 overflow-y-auto">
              {gains.map((g) => (
                <li key={g} className="flex items-start gap-2 font-body text-sm text-foreground">
                  <Check className="mt-0.5 h-4 w-4 shrink-0" strokeWidth={1.5} aria-hidden="true" />{g}
                </li>
              ))}
            </ul>
          </div>
        )}
        <Button onClick={confirm} disabled={busy || ui.previewOn} className="min-h-11 w-full rounded-full font-bold">
          {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          {showNewTrial ? (es ? "Empezar prueba gratis" : "Start free trial") : (es ? `Subir a ${ui.planName(target?.plan)}` : `Upgrade to ${ui.planName(target?.plan)}`)}
        </Button>
        {ui.previewOn && <p className="text-center font-body text-[11px] text-muted-foreground">{es ? "En vista previa no se cobra ni se cambia nada." : "Nothing is charged or changed in preview."}</p>}
      </DialogContent>
    </Dialog>
  );
}
